import { Inject } from '@odinjs/odin';
import { DatePickerAPI, ModalService } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnMount, OnNavigate } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT, ISO_YEAR_MONTH_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment/moment.js';
import { AnotherPeriodAliases, FragmentNames } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import PseudonymIntegratedScheduleService from './pseudonym-service/pseudonym-integrated-schedule-service';
import PseudonymStepSixService from './pseudonym-service/pseudonym-step-6-service';

interface SchedulingConfig {
  allowSelectDesiredDate: boolean;
  desiredDateCustomMessage: string;
  scheduleRangeAllowed: number;
  schedulingAllowedDays: number;
  schedulingFinalDate: Date;
}

@Controller({ component: AnotherPeriodAliases.LAYOUT_SCHEDULE_PERIOD, fragment: FragmentNames.ANOTHER_PERIOD })
export default class PseudonymAppointmentsAnotherPeriodController extends PseudonymAppointmentsBaseController {

  @Inject(AnotherPeriodAliases.DATE_PICKER_SCHEDULE_RANGE)
    datePickerScheduleRange!: DatePickerAPI;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  @Inject('PseudonymStepSixService')
    pseudonymStepSixService!: PseudonymStepSixService;

  @OnMount({ target: AnotherPeriodAliases.DATE_PICKER_SCHEDULE_RANGE })
  handleMountDatePicker(): void {
    HttpService.post(APPLICATION_ENDPOINTS.GET_NEW_APPOINTMENT_SCHEDULE_CONFIG)
      .then(response => {
        const schedulingConfig = response.body as SchedulingConfig;
        const today = moment().utc().toISOString();
        const finalDate = schedulingConfig.schedulingFinalDate.toString();

        this.datePickerScheduleRange.setIntervalThreshold(schedulingConfig.scheduleRangeAllowed);
        this.datePickerScheduleRange.addRange(today, finalDate);
        this.pseudonymStepSixService.setInitialRangeDatePickerToContext(today, finalDate);

        PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.start = today;
        PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.end = finalDate;
        PseudonymAppointmentModel.stepSix.allowSelectDesiredDate = schedulingConfig.allowSelectDesiredDate;
        PseudonymAppointmentModel.stepSix.desiredDateCustomMessage = schedulingConfig.desiredDateCustomMessage;

        if (this.getIsRescheduleContext() && !PseudonymAppointmentModel.stepFive.location) {
          this.setAvailableCalendarDaysForReschedule();
        }
      });
  }

  private setAvailableCalendarDaysForReschedule(): void {
    this.clearValueDatePickerScheduleRange();
    this.verifyAvailableCalendarDays();
  }

  private clearValueDatePickerScheduleRange(): void {
    this.datePickerScheduleRange.setInitialDate(moment().utc().toISOString());
    this.datePickerScheduleRange.setValue([]);
  }

  @OnChange({ target: AnotherPeriodAliases.DATE_PICKER_SCHEDULE_RANGE })
  async handleChangeDateRange(event: ChangeEvent<Array<string>>): Promise<void> {
    if (event && event.value.length > 0) {
      PseudonymAppointmentModel.stepAnotherPeriod.hasValues = true;
      const rangeDate = event.value || [];
      const startDate = rangeDate[0];
      const endDate = rangeDate.length === 1 ? rangeDate[0] : rangeDate[1];

      if (rangeDate.length >= 1) {
        PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.start = startDate > endDate ? endDate : startDate;
        PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.end = startDate > endDate ? startDate : endDate;
      }

      const modalId = this.context.get('modalId') as string;
      PseudonymAppointmentModel.stepAnotherPeriod.isAnotherPeriodSelected = true;
      ModalService.close(modalId);
    }
  }

  @OnNavigate({ target: AnotherPeriodAliases.DATE_PICKER_SCHEDULE_RANGE })
  handleNavigateDatePicker(event: AbortableNavigateEvent<string>): void {
    const currentDate = moment.utc();
    const goToDate = moment.utc(event.to?.[0]);
    const lastCalendarDate = goToDate.add(1, 'months');

    if (lastCalendarDate > currentDate) {
      const ranges = this.datePickerScheduleRange.getRanges();
      for (const range of ranges) {
        if (range.startDate.includes(lastCalendarDate.format(ISO_YEAR_MONTH_FORMAT))) {
          return;
        }
      }

      this.verifyAvailableCalendarDays(lastCalendarDate.format(ISO_DATE_FORMAT));
    }
  }

  verifyAvailableCalendarDays(lastCalendarDate = ''): void {
    const stepTwo = PseudonymAppointmentModel.stepTwo;
    const procedure = stepTwo.procedures[0];
    if (!stepTwo.isOnlyConsultation
      || stepTwo.procedures.length !== 1
      || !procedure.specialtySequence
      || !procedure.physicianSequence) {
      this.pseudonymStepSixService.restoreInitialRangeDatePickerFromContext();
      return;
    }

    const datePickerScheduleRange = PseudonymAppointmentModel.stepAnotherPeriod.datePickerScheduleRange;
    datePickerScheduleRange.clearRanges();
    datePickerScheduleRange.setLoading(true);

    this.pseudonymStepSixService.getAvailableCalendarDays(lastCalendarDate)
      .then(response => this.pseudonymStepSixService.handleAvailableCalendarDaysResponse(response))
      .catch(() => this.pseudonymStepSixService.restoreInitialRangeDatePickerFromContext())
      .finally(() => datePickerScheduleRange.setLoading(false));
  }
}
